import React, { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { loadGoogleMapsScript } from '../services/googleMapsApi';
import '../types/google-maps.d.ts';
import { createLogger } from '../utils/logger';


interface Activity {
  id: string;
  name: string;
  dayIndex?: number; // Add dayIndex for different day markers
  isRestaurant?: boolean; // Flag to indicate if this is a restaurant
  globalIndex?: number; // Global index across all days for numbering
  activity_id?: string; // Activity ID for database updates
  fullActivity?: {
    address?: string;
    location_name?: string;
    latitude?: number;
    longitude?: number;
    description?: string;
    duration?: string;
    cost?: string;
    working_hours?: string;
    category?: string[];
    activity_id?: string; // Activity ID from database
  };
}

interface DayMapProps {
  activities: Activity[];
  destinationName: string;
  dayNumber: number;
  selectedActivityId?: string | null;
  onActivitySelect?: (activityId: string) => void;
  showRestaurants?: boolean; // Whether to show restaurants on the map
  forceFullscreen?: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}


const log = createLogger('DayMap');

const FALLBACK_MARKER_ICONS = {
  default: 'https://maps.gstatic.com/mapfiles/ms2/micons/red-dot.png',
  selected: 'https://maps.gstatic.com/mapfiles/ms2/micons/blue-dot.png',
  restaurant: 'https://maps.gstatic.com/mapfiles/ms2/micons/orange-dot.png',
  restaurantSelected: 'https://maps.gstatic.com/mapfiles/ms2/micons/yellow-dot.png',
};

const getFallbackIcon = (isRestaurant: boolean, isSelected: boolean) => {
  if (isRestaurant) {
    return isSelected ? FALLBACK_MARKER_ICONS.restaurantSelected : FALLBACK_MARKER_ICONS.restaurant;
  }
  return isSelected ? FALLBACK_MARKER_ICONS.selected : FALLBACK_MARKER_ICONS.default;
};

const isValidLatLng = (position?: google.maps.LatLng | null): position is google.maps.LatLng => {
  if (!position || typeof position.lat !== 'function' || typeof position.lng !== 'function') {
    return false;
  }

  try {
    return Number.isFinite(position.lat()) && Number.isFinite(position.lng());
  } catch {
    return false;
  }
};

const DayMap: React.FC<DayMapProps> = ({
  activities,
  destinationName,
  dayNumber,
  selectedActivityId = null,
  onActivitySelect,
  showRestaurants = false,
  forceFullscreen,
  onFullscreenChange,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<(google.maps.marker.AdvancedMarkerElement | google.maps.Marker)[]>([]);
  const isMountedRef = useRef(true);
  const uniqueId = useRef(`map-${Math.random().toString(36).substr(2, 9)}`);
  const isCreatingMapRef = useRef(false);
  const cancelMapCreationRef = useRef(false);
  const markerLocationsRef = useRef<{ position: google.maps.LatLng; activity: Activity; index: number; }[]>([]);
  const currentActivityIdRef = useRef<string | null>(null);
  const supportsAdvancedMarkersRef = useRef(false);
  const containerReadyRef = useRef(false);
  const pendingActivitiesRef = useRef<Activity[]>([]); // Store pending activities for batched updates
  const lastUpdateTimeRef = useRef<number>(0); // Track last update time
  const hasInitialMapRef = useRef(false); // Track if we've created the initial map
  const renderedActivitiesKeyRef = useRef<string | null>(null);

  const [isLoading, setIsLoading] = useState(false); // Start with false to show map container immediately
  const [isMapApiLoaded, setIsMapApiLoaded] = useState(() => {
    // Check if Google Maps is already loaded globally
    return !!(window.google && window.google.maps && window.google.maps.marker);
  });
  const [isLoadingPins, setIsLoadingPins] = useState(false); // Separate state for pin loading
  const [error, setError] = useState<string | null>(null);
  const [isContainerReady, setIsContainerReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(() => Boolean(forceFullscreen));
  const theme = useTheme();

  // Cache for geocoded coordinates to avoid repeated API calls
  const geocodeCache = useRef<Map<string, google.maps.LatLng>>(new Map());

  // Load geocode cache from localStorage on mount
  useEffect(() => {
    try {
      const cachedData = localStorage.getItem('geocodeCache');
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        Object.entries(parsed).forEach(([key, value]: [string, any]) => {
          if (value && typeof value.lat === 'number' && typeof value.lng === 'number' && window.google?.maps) {
            geocodeCache.current.set(key, new google.maps.LatLng(value.lat, value.lng));
          }
        });
        log.debug(`Loaded ${geocodeCache.current.size} cached geocode results from localStorage`);
      }
    } catch (error) {
      log.warn('Failed to load geocode cache from localStorage:', error);
    }
  }, []);

  // Save geocode to cache and localStorage
  const cacheGeocode = useCallback((key: string, location: google.maps.LatLng) => {
    geocodeCache.current.set(key, location);

    // Save to localStorage (limit cache size to prevent quota issues)
    try {
      const cacheObj: Record<string, {lat: number, lng: number}> = {};
      let count = 0;
      geocodeCache.current.forEach((value, cacheKey) => {
        if (count < 500) { // Limit to 500 entries
          cacheObj[cacheKey] = { lat: value.lat(), lng: value.lng() };
          count++;
        }
      });
      localStorage.setItem('geocodeCache', JSON.stringify(cacheObj));
    } catch (error) {
      log.warn('Failed to save geocode cache to localStorage:', error);
    }
  }, []);

  // Save geocoded coordinates to MongoDB
  const saveCoordinatesToDB = useCallback(async (activityId: string, coordinates: {lat: number, lng: number}) => {
    try {
      const response = await fetch('/api/update-activity-coordinates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          activityId,
          coordinates,
        }),
      });

      if (!response.ok) {
        log.debug(`Failed to save coordinates for activity ${activityId}`);
      }
    } catch (error) {
      log.debug(`Error saving coordinates for activity ${activityId}:`, error);
    }
  }, []);

  const updateFullscreen = useCallback((next: boolean) => {
    setIsFullscreen(next);
    if (onFullscreenChange) {
      onFullscreenChange(next);
    }
  }, [onFullscreenChange]);

  // Handle escape key to exit fullscreen and body scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        updateFullscreen(false);
        setTimeout(() => {
          if (mapInstanceRef.current) {
            google.maps.event.trigger(mapInstanceRef.current, 'resize');
          }
        }, 100);
      }
    };

    // Lock body scroll when fullscreen
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('map-fullscreen');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('map-fullscreen');
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      document.body.classList.remove('map-fullscreen');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);

  // Sync fullscreen with external control
  useEffect(() => {
    if (typeof forceFullscreen === 'boolean' && forceFullscreen !== isFullscreen) {
      updateFullscreen(forceFullscreen);
    }
  }, [forceFullscreen, isFullscreen, updateFullscreen]);

  // Memoize activities with valid addresses (optionally excluding restaurants)
  const validActivities = useMemo(() => {
    return activities.filter(activity => {
      // Filter out restaurants unless showRestaurants is true
      if (activity.isRestaurant && !showRestaurants) {
        return false;
      }
      const address = activity.fullActivity?.address;
      return address && 
             address !== "Address to be determined" &&
             address !== `Location in ${destinationName}` &&
             address.trim() !== '';
    });
  }, [activities, destinationName, showRestaurants]);

  // Memoize activities key for change detection
  const activitiesKey = useMemo(() => {
    return validActivities.map(a => 
      `${a.id}-${a.fullActivity?.address}-${a.fullActivity?.latitude}-${a.fullActivity?.longitude}`
    ).join('|');
  }, [validActivities]);

  // Color palette for different days - memoized to prevent recreating on every render
  const dayColors = useMemo(() => [
    '#FF385C', // Day 0 - Brand Pink
    '#00A699', // Day 1 - Teal
    '#FF9500', // Day 2 - Amber
    '#484848', // Day 3 - Charcoal
    '#C81E42', // Day 4 - Deep Pink
    '#2ACEBA', // Day 5 - Light Teal
    '#717171', // Day 6 - Muted Grey
    '#FF6F85', // Day 7 - Blush
    '#9C27B0', // Day 8 - Purple Accent
    '#FFB347', // Day 9 - Warm Orange
  ], []);

  // Create numbered marker content using Advanced Marker format
  const createNumberedMarkerContent = useCallback((number: number, dayIndex: number = 0, isSelected: boolean = false, isRestaurant: boolean = false) => {
    const pinGlyph = document.createElement('div');
    // Handle negative dayIndex by using absolute value and modulo
    const safeDayIndex = dayIndex < 0 ? Math.abs(dayIndex) % dayColors.length : dayIndex;
    const dayColor = dayColors[safeDayIndex % dayColors.length];
    const restaurantColor = '#FF7A50'; // Softer coral tone for restaurants
    const selectedColor = theme.palette.primary.main;
    const backgroundColor = isRestaurant ? restaurantColor : (isSelected ? selectedColor : dayColor);
    
    pinGlyph.className = 'w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shadow-lg border-2 border-white';
    pinGlyph.textContent = isRestaurant ? '🍽️' : number.toString();
    
    // Special styling for restaurant markers - icon only with subtle shadow
    if (isRestaurant) {
      pinGlyph.style.cssText = `
        width: ${isSelected ? '36px' : '34px'};
        height: ${isSelected ? '36px' : '34px'};
        background: linear-gradient(135deg, ${restaurantColor} 0%, #FF4F6D 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${isSelected ? '20px' : '18px'};
        font-weight: normal;
        color: #FFFFFF;
        box-shadow: ${isSelected ? '0 6px 16px rgba(255, 122, 80, 0.28)' : '0 3px 8px rgba(255, 122, 80, 0.22)'};
        border: ${isSelected ? '2px solid rgba(255, 255, 255, 0.8)' : '1.5px solid rgba(255, 255, 255, 0.65)'};
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        cursor: pointer;
        transition: all 0.2s ease;
        transform: ${isSelected ? 'scale(1.15)' : 'scale(1)'};
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.18));
      `;
    } else {
      pinGlyph.style.cssText = `
        width: ${isSelected ? '30px' : '32px'};
        height: ${isSelected ? '30px' : '32px'};
        background-color: ${backgroundColor};
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${isSelected ? '16px' : '14px'};
        font-weight: bold;
        box-shadow: ${isSelected ? '0 6px 16px rgba(255, 107, 53, 0.4)' : `0 2px 6px ${backgroundColor}40`};
        border: ${isSelected ? '2px solid white' : '1.5px solid white'};
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        cursor: pointer;
        transition: all 0.2s ease;
      `;
    }
    return pinGlyph;
  }, [dayColors, theme.palette.primary.main]);


    // Initialize Google Maps using shared loading utility
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initializeGoogleMaps = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }

    // Check if Google Maps API is already available
    if (window.google && window.google.maps && window.google.maps.marker) {
      if (!isMapApiLoaded) {
        setIsMapApiLoaded(true);
      }
      setIsLoading(false);
      return;
    }

    if (!isLoading) {
      setIsLoading(true);
    }

    log.debug("Loading Google Maps using shared utility");

    try {
      // Use the shared loading utility that handles deduplication
      await loadGoogleMapsScript();

      if (isMountedRef.current) {
        setIsMapApiLoaded(true);
        setIsLoading(false);
        log.debug("Google Maps loaded successfully");
      }
    } catch (error) {
      log.error("Failed to load Google Maps:", error);
      if (isMountedRef.current) {
        setError((error as Error)?.message || 'Failed to load Google Maps');
        setIsLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create map with activities using Advanced Marker Elements  
  const createMapWithActivities = useCallback(async (activities: Activity[]) => {
    // Cancel any ongoing map creation
    if (isCreatingMapRef.current) {
      log.debug("Cancelling previous map creation");
      cancelMapCreationRef.current = true;
      // Wait a bit for the previous creation to abort
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    // Reset cancel flag for this new creation
    cancelMapCreationRef.current = false;
    isCreatingMapRef.current = true;
    setIsLoadingPins(true); // Set pin loading state
    
    if (!isMountedRef.current || cancelMapCreationRef.current || !window.google?.maps) {
      log.debug("Early return from createMapWithActivities - not ready");
      if (isMountedRef.current) {
        setIsLoadingPins(false);
        setIsLoading(false);
      }
      isCreatingMapRef.current = false;
      return;
    }

    const supportsAdvancedMarkers = Boolean(window.google?.maps?.marker?.AdvancedMarkerElement);
    supportsAdvancedMarkersRef.current = supportsAdvancedMarkers;
    if (!supportsAdvancedMarkers) {
      log.debug("AdvancedMarkerElement unavailable - falling back to default markers");
    }

    log.debug(`Creating map with ${activities.length} activities`);
    
    // Allow creating map even with 0 activities - it will show the destination and update later
    if (activities.length === 0) {
      log.debug("No activities yet - creating empty map centered on destination");
    }

        // Ensure we have a valid container with comprehensive checks
    log.debug(`[${uniqueId.current}] mapRef.current exists:`, !!mapRef.current);
    log.debug(`[${uniqueId.current}] mapRef.current in DOM:`, mapRef.current?.isConnected);
    log.debug(`[${uniqueId.current}] mapRef.current offsetParent:`, mapRef.current?.offsetParent);
    
    if (!mapRef.current || !mapRef.current.isConnected) {
      log.debug(`[${uniqueId.current}] Map container not properly available in createMapWithActivities`);
      if (isMountedRef.current) {
        setError('Map container not properly rendered');
        setIsLoadingPins(false);
        setIsLoading(false);
      }
      isCreatingMapRef.current = false;
      return;
    }
    
    // Allow creation even if offsetParent is null (can happen with CSS transforms or position changes)
    if (mapRef.current.offsetParent === null) {
      log.debug(`[${uniqueId.current}] Container has no offsetParent, but proceeding with map creation`);
    }
    
    // Double-check container dimensions
    const containerWidth = mapRef.current.offsetWidth;
    const containerHeight = mapRef.current.offsetHeight;
    log.debug(`[${uniqueId.current}] Container dimensions: ${containerWidth}x${containerHeight}`);

    if (containerWidth === 0 || containerHeight === 0) {
      log.debug(`[${uniqueId.current}] Container has zero dimensions - waiting for resize`);
      if (isMountedRef.current) {
        setIsLoadingPins(false);
        setIsLoading(false);
      }
      isCreatingMapRef.current = false;
      return;
    }

    try {
      log.debug(`[${uniqueId.current}] Starting geocoding process for`, activities.length, "activities");
      const geocoder = new google.maps.Geocoder();
      const bounds = new google.maps.LatLngBounds();

      // Statistics tracking
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let _fromDatabase = 0;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let _fromLocalStorage = 0;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let _fromGoogleAPI = 0;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let _failed = 0;

      // Process activities in parallel with rate limiting
      const geocodePromises = activities.map(async (activity, index) => {
        const { address, location_name, latitude, longitude } = activity.fullActivity || {};
        const activityNumber = activity.globalIndex || (index + 1); // Use globalIndex if available

        // Use existing coordinates if available from database
        if (typeof latitude === 'number' && typeof longitude === 'number' &&
            !isNaN(latitude) && !isNaN(longitude)) {
          const position = new google.maps.LatLng(latitude, longitude);
          _fromDatabase++;

          return {
            position,
            activity,
            index: activityNumber
          };
        }

        // Geocode address
        if (address) {
          const searchQuery = `${location_name || activity.name}, ${address}, ${destinationName}`;

          // Check localStorage cache first
          const cacheKey = `${searchQuery.toLowerCase()}`;
          const cachedPosition = geocodeCache.current.get(cacheKey);
          if (cachedPosition) {
            _fromLocalStorage++;
            return {
              position: cachedPosition,
              activity,
              index: activityNumber
            };
          }

          try {
            // Add delay to prevent rate limiting
            await new Promise(resolve => setTimeout(resolve, index * 100));

            const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
              // Add timeout for geocoding request
              const timeoutId = setTimeout(() => {
                reject(new Error(`Geocoding timeout for ${searchQuery}`));
              }, 5000);

              geocoder.geocode({ address: searchQuery }, (results, status) => {
                clearTimeout(timeoutId);

                if (status === 'OK' && results && results.length > 0) {
                  resolve(results);
                } else {
                  reject(new Error(`Geocoding failed for ${searchQuery}: ${status}`));
                }
              });
            });

            const position = result[0].geometry.location;
            _fromGoogleAPI++;

            // Cache the geocoded position
            cacheGeocode(cacheKey, position);

            // Save to database if we have an activity_id
            const activityIdField = (activity as any).fullActivity?.activity_id ||
                                    (activity as any).activity_id;

            if (activityIdField) {
              const coordinates = { lat: position.lat(), lng: position.lng() };
              saveCoordinatesToDB(activityIdField, coordinates).catch(() => {
                // Silently fail - not critical
              });
            }

            return {
              position,
              activity,
              index: activityNumber
            };
          } catch (error) {
            _failed++;
            log.debug(`Could not geocode "${activity.name}"`);
            return null;
          }
        }

        return null;
      });

      log.debug("Waiting for geocoding results...");
      
      // Add overall timeout for the entire geocoding process
      const geocodingTimeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Geocoding timeout - taking too long')), 15000); // 15 second timeout
      });
      
      let validLocations: { position: google.maps.LatLng; activity: Activity; index: number; }[] = [];
      
      try {
        const results = await Promise.race([
          Promise.allSettled(geocodePromises),
          geocodingTimeout
        ]);
        
        log.debug("Promise.allSettled results:", results.map((r, i) => ({
          index: i,
          status: r.status,
          activity: activities[i]?.name,
          reason: r.status === 'rejected' ? (r.reason as Error)?.message : 'success'
        })));
        
        validLocations = results
          .filter((result): result is PromiseFulfilledResult<{ position: google.maps.LatLng; activity: Activity; index: number; }> =>
            result.status === 'fulfilled' && result.value !== null)
          .map(result => result.value)
          .filter(location => {
            const isValidLocation = isValidLatLng(location.position);
            if (!isValidLocation) {
              log.debug(`Ignoring invalid map position for "${location.activity.name}"`);
            }
            return isValidLocation;
          });

        log.debug(`Geocoded ${validLocations.length}/${activities.length} activities`);
      } catch (error) {
        log.error("Geocoding process failed or timed out:", error);
        if (isMountedRef.current && mapRef.current && mapRef.current.isConnected) {
          setError('Map loading timed out. Please try refreshing the page.');
          setIsLoadingPins(false);
          setIsLoading(false);
        }
        return;
      }

      // Check if cancelled after geocoding
      if (cancelMapCreationRef.current || !isMountedRef.current) {
        log.debug("Map creation cancelled after geocoding");
        return;
      }

      if (validLocations.length === 0) {
        log.debug("No valid locations found - showing map centered on destination");

        // Always show a basic map with city center, even when no activities are available yet
        if (isMountedRef.current) {
          log.debug("Creating empty map centered on destination city");

          // Create a basic map centered on the destination city
          const cityGeocoder = new google.maps.Geocoder();
          try {
            const cityResult = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
              cityGeocoder.geocode({ address: destinationName }, (results, status) => {
                if (status === 'OK' && results && results.length > 0) {
                  resolve(results);
                } else {
                  reject(new Error(`City geocoding failed: ${status}`));
                }
              });
            });

            const cityCenter = cityResult[0].geometry.location;
            if (!isValidLatLng(cityCenter)) {
              throw new Error('City geocoding returned an invalid location');
            }

            const map = new google.maps.Map(mapRef.current, {
              zoom: 12,
              center: cityCenter,
              mapId: 'DEMO_MAP_ID',
              disableDefaultUI: true,
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              gestureHandling: 'greedy'
            });

            mapInstanceRef.current = map;
            setIsLoadingPins(false);
            setIsLoading(false);
            setError(null);
            log.debug("Empty map created successfully - will update when activities arrive");
            return;

          } catch (cityError) {
            log.error("City geocoding failed:", cityError);
            // Continue to show error below
          }
        }

        // Only show error if we're mounted and couldn't create the city-centered map
        if (isMountedRef.current) {
          setError('Unable to locate the destination on the map. Please try again.');
          setIsLoadingPins(false);
          setIsLoading(false);
        }
        return;
      }

      // Calculate bounds using only positions that remain valid after async geocoding.
      const boundedLocations = validLocations.filter(location => isValidLatLng(location.position));
      boundedLocations.forEach(location => {
        bounds.extend(location.position);
      });

      const boundsCenter = bounds.getCenter();
      const mapCenter = isValidLatLng(boundsCenter) ? boundsCenter : boundedLocations[0]?.position;

      if (!isValidLatLng(mapCenter)) {
        log.error(`[${uniqueId.current}] Could not determine a valid map center`);
        if (isMountedRef.current) {
          setError('Unable to locate activities on the map. Please try again.');
          setIsLoadingPins(false);
          setIsLoading(false);
        }
        return;
      }

      // Create map with modern styling (same as working example)
      log.debug(`[${uniqueId.current}] Creating Google Map with`, boundedLocations.length, "locations");
      log.debug(`[${uniqueId.current}] Map center:`, { lat: mapCenter.lat(), lng: mapCenter.lng() });
      
      // Final validation before creating the map
      if (!mapRef.current || !mapRef.current.isConnected) {
        log.error(`[${uniqueId.current}] Container became invalid before map creation`);
        if (isMountedRef.current) {
          setError('Map container became invalid');
          setIsLoadingPins(false);
          setIsLoading(false);
        }
        isCreatingMapRef.current = false;
        return;
      }
      
      log.debug(`[${uniqueId.current}] Container final check passed, creating map...`);
      const map = new google.maps.Map(mapRef.current, {
        zoom: 11, // More zoomed out
        center: mapCenter,
        mapId: 'DEMO_MAP_ID',
        disableDefaultUI: true,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        gestureHandling: 'greedy',
        clickableIcons: false, // Disable default POI clicks to prevent interference
        draggable: true,
      });

      mapInstanceRef.current = map;

      // Center on selected activity if specified, otherwise fit bounds
      if (selectedActivityId) {
        const selectedLocation = boundedLocations.find(location => location.activity.id === selectedActivityId);
        if (selectedLocation) {
          map.setCenter(selectedLocation.position);
          map.setZoom(14); // Less zoomed in
        } else {
          // Fallback to first activity if selected activity not found
          if (boundedLocations.length > 0) {
            map.setCenter(boundedLocations[0].position);
            map.setZoom(14); // Less zoomed in
          }
        }
      } else {
        // Default behavior: fit bounds or center on first activity
        if (boundedLocations.length > 1) {
          const padding = { top: 80, right: 80, bottom: 80, left: 80 };
          map.fitBounds(bounds, padding);
        } else if (boundedLocations.length === 1) {
          map.setCenter(boundedLocations[0].position);
          map.setZoom(14); // Less zoomed in
        }
      }

      // Check if cancelled before creating markers
      if (cancelMapCreationRef.current || !isMountedRef.current) {
        log.debug("Map creation cancelled before creating markers");
        return;
      }

      // Clear existing markers
      markersRef.current.forEach(marker => {
        if ('map' in marker && marker.map) {
          marker.map = null;
        } else if ('setMap' in marker) {
          (marker as any).setMap(null);
        }
      });
      markersRef.current = [];

      // Create markers (Advanced Marker Elements when supported, otherwise default markers)
      const newMarkers: (google.maps.marker.AdvancedMarkerElement | google.maps.Marker)[] = boundedLocations.map(location => {
        const isSelected = selectedActivityId === location.activity.id;
        const dayIndex = location.activity.dayIndex || 0;
        const isRestaurant = location.activity.isRestaurant || false;
        const markerContent = createNumberedMarkerContent(location.index, dayIndex, isSelected, isRestaurant);

        const locationLabel = isRestaurant ? 'Restaurant' : 'Activity';
        if (supportsAdvancedMarkers) {
          // Make marker content clickable with pointer events
          if (markerContent instanceof HTMLElement) {
            markerContent.style.pointerEvents = 'auto';
            markerContent.style.cursor = 'pointer';
            markerContent.style.userSelect = 'none';
            markerContent.style.touchAction = 'manipulation'; // Prevent double-tap zoom
          }

          const markerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
            position: location.position,
            map,
            title: `${locationLabel}: ${location.activity.name}`,
            content: markerContent,
            zIndex: isRestaurant ? 0 : (isSelected ? 2 : 1),
            gmpClickable: true, // Ensure marker is clickable
          };

          const marker = new google.maps.marker.AdvancedMarkerElement(markerOptions);

          // Add click listener to trigger activity selection (opens full card)
          // Use both click and pointerdown for better mobile support
          let clickHandled = false;
          const handleMarkerClick = (e: any) => {
            // Prevent multiple rapid clicks
            if (clickHandled) return;
            clickHandled = true;
            setTimeout(() => { clickHandled = false; }, 500);
            
            // Prevent map from handling the event
            if (e && e.stop) {
              e.stop();
            }
            if (e && e.domEvent) {
              e.domEvent.stopPropagation();
              e.domEvent.preventDefault();
            }
            log.debug('📍 [DayMap] Marker clicked:', location.activity.id, location.activity.name);
            if (onActivitySelect) {
              log.debug('📍 [DayMap] Calling onActivitySelect with:', location.activity.id);
              // Use setTimeout to ensure event propagation is complete
              setTimeout(() => {
                onActivitySelect(location.activity.id);
              }, 0);
            } else {
              log.warn('📍 [DayMap] onActivitySelect callback not provided');
            }
          };
          
          marker.addListener('click', handleMarkerClick);
          // Also listen to pointerdown for better mobile touch support
          if ('addListener' in marker) {
            marker.addListener('pointerdown', handleMarkerClick);
          }

          return marker;
        }

        const fallbackMarker = new google.maps.Marker({
          position: location.position,
          map,
          title: `${locationLabel}: ${location.activity.name}`,
          icon: getFallbackIcon(isRestaurant, isSelected),
          zIndex: isRestaurant ? 0 : (isSelected ? 2 : 1),
          clickable: true, // Ensure marker is clickable
          optimized: false, // Disable optimization for better touch handling
        });

        // Handle click with event prevention for mobile
        let clickHandled = false;
        const handleFallbackClick = (e: any) => {
          // Prevent multiple rapid clicks
          if (clickHandled) return;
          clickHandled = true;
          setTimeout(() => { clickHandled = false; }, 500);
          
          // Prevent map from handling the event
          if (e && e.stop) {
            e.stop();
          }
          if (e && e.domEvent) {
            e.domEvent.stopPropagation();
            e.domEvent.preventDefault();
          }
          log.debug('📍 [DayMap] Fallback marker clicked:', location.activity.id, location.activity.name);
          if (onActivitySelect) {
            log.debug('📍 [DayMap] Calling onActivitySelect with:', location.activity.id);
            // Use setTimeout to ensure event propagation is complete
            setTimeout(() => {
              onActivitySelect(location.activity.id);
            }, 0);
          } else {
            log.warn('📍 [DayMap] onActivitySelect callback not provided');
          }
        };
        
        fallbackMarker.addListener('click', handleFallbackClick);

        return fallbackMarker;
      });

      markersRef.current = newMarkers;
      
      // Store marker locations for later use in centering
      markerLocationsRef.current = boundedLocations;
      
      // Reset current activity tracking when markers are recreated
      currentActivityIdRef.current = null;

      if (isMountedRef.current) {
        setIsLoadingPins(false);
        setIsLoading(false);
        setError(null);
      }

    } catch (error) {
      log.error('Error creating map:', error);
      if (isMountedRef.current) {
        setError('Failed to create map');
        setIsLoadingPins(false);
        setIsLoading(false);
      }
    } finally {
      isCreatingMapRef.current = false;
    }
  }, [destinationName, selectedActivityId, onActivitySelect, cacheGeocode, createNumberedMarkerContent, saveCoordinatesToDB]);

  // Check container readiness using useLayoutEffect for immediate DOM access
  useLayoutEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let intervalId: number | null = null;
    let cancelled = false;

    const updateContainerState = (ready: boolean) => {
      containerReadyRef.current = ready;
      setIsContainerReady((prev) => (prev === ready ? prev : ready));
    };

    const checkContainer = () => {
      if (cancelled) {
        return;
      }
      const element = mapRef.current;
      if (element && element.isConnected) {
        const hasSize = element.offsetWidth > 0 && element.offsetHeight > 0;
        if (hasSize) {
          log.debug(`[${uniqueId.current}] Container ready with dimensions ${element.offsetWidth}x${element.offsetHeight}`);
          updateContainerState(true);
          return;
        }
      }

      log.debug(`[${uniqueId.current}] Container not ready (missing connection or dimensions)`);
      updateContainerState(false);
    };

    const ensureObserver = () => {
      if (resizeObserver || typeof ResizeObserver === 'undefined') {
        return;
      }
      const element = mapRef.current;
      if (!element) {
        return;
      }
      resizeObserver = new ResizeObserver(() => checkContainer());
      resizeObserver.observe(element);
    };

    checkContainer();
    ensureObserver();

    intervalId = window.setInterval(() => {
      checkContainer();
      ensureObserver();
    }, 200);

    const timeoutId = window.setTimeout(() => {
      if (!containerReadyRef.current && isMountedRef.current) {
        log.debug(`[${uniqueId.current}] Container readiness timeout - may be hidden`);
      }
    }, 5000);

    return () => {
      cancelled = true;
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
      clearTimeout(timeoutId);
    };
  }, []);

  // Initialize map when all conditions are met
  // Use 20-second interval for batched updates instead of updating on every pin change
  useEffect(() => {
    log.debug(`[${uniqueId.current}] Initialize map useEffect triggered`);
    log.debug(`[${uniqueId.current}] isMapApiLoaded:`, isMapApiLoaded);
    log.debug(`[${uniqueId.current}] isContainerReady:`, isContainerReady);
    log.debug(`[${uniqueId.current}] validActivities.length:`, validActivities.length);
    log.debug(`[${uniqueId.current}] activities.length:`, activities.length);

    // Only require API loaded and container ready - create map immediately
    // The map will show and update as activities with addresses arrive
    if (!isMapApiLoaded || !isContainerReady) {
      log.debug(`[${uniqueId.current}] Not creating map - API or container not ready`);
      if (isMountedRef.current) {
        setIsLoadingPins(false);
        setIsLoading(false);
      }
      return;
    }

    // Store current activities for interval updates
    pendingActivitiesRef.current = validActivities;

    const mapRenderKey = `${destinationName}|${activitiesKey}`;
    const shouldCreateMap = !hasInitialMapRef.current || renderedActivitiesKeyRef.current !== mapRenderKey;

    if (shouldCreateMap) {
      const action = hasInitialMapRef.current ? 'Updating' : 'Creating initial';
      log.debug(`[${uniqueId.current}] ${action} map with ${validActivities.length} valid activities (out of ${activities.length} total)`);
      hasInitialMapRef.current = true;
      renderedActivitiesKeyRef.current = mapRenderKey;
      lastUpdateTimeRef.current = Date.now();
      createMapWithActivities(validActivities);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapApiLoaded, isContainerReady, activitiesKey, activities.length, destinationName]);

  // REMOVED: 20-second interval for batched map updates
  // This was causing excessive API calls. The map now only updates when activities actually change
  // via the activitiesKey dependency in the main useEffect above.

  // Load Google Maps on mount - avoid circular dependency by not including initializeGoogleMaps as dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    log.debug("Load Google Maps useEffect triggered");
    log.debug("activities.length:", activities.length);
    log.debug("isMapApiLoaded:", isMapApiLoaded);

    // Always load Google Maps API, even if no activities yet - map should be ready to show
    if (!isMapApiLoaded) {
      log.debug("Calling initializeGoogleMaps");
      initializeGoogleMaps().catch(error => {
        log.error("initializeGoogleMaps failed:", error);
        if (isMountedRef.current) {
          setError(`Map initialization failed: ${error.message}`);
          setIsLoading(false);
        }
      });
    } else {
      log.debug("Google Maps already loaded");
      setIsLoading(false);
      setIsLoadingPins(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapApiLoaded, initializeGoogleMaps]);

  // Handle selectedActivityId changes to center map and update marker styling
  useEffect(() => {
    if (!mapInstanceRef.current || !selectedActivityId || markersRef.current.length === 0 || markerLocationsRef.current.length === 0) {
      return;
    }

    // Skip if this activity is already centered
    if (currentActivityIdRef.current === selectedActivityId) {
      return;
    }

    const selectedLocationIndex = markerLocationsRef.current.findIndex(
      location => location.activity.id === selectedActivityId
    );

    const updateMarkerStyles = (highlightIndex: number | null) => {
      if (!markerLocationsRef.current.length) {
        return;
      }

      if (supportsAdvancedMarkersRef.current) {
        markersRef.current.forEach((marker, index) => {
          const isSelected = highlightIndex !== null && index === highlightIndex;
          const activityMeta = markerLocationsRef.current[index].activity;
          const dayIndex = activityMeta.dayIndex || 0;
          const isRestaurant = activityMeta.isRestaurant || false;
          const markerContent = createNumberedMarkerContent(
            markerLocationsRef.current[index].index,
            dayIndex,
            isSelected,
            isRestaurant
          );

          if ('content' in marker) {
            marker.content = markerContent;
          }

          if ('zIndex' in marker) {
            marker.zIndex = isRestaurant ? 0 : (isSelected ? 2 : 1);
          }
        });
        return;
      }

      markersRef.current.forEach((marker, index) => {
        const isSelected = highlightIndex !== null && index === highlightIndex;
        const activityMeta = markerLocationsRef.current[index].activity;
        const isRestaurant = activityMeta.isRestaurant || false;
        const fallbackMarker = marker as google.maps.Marker & {
          setIcon?: (icon: string) => void;
          setZIndex?: (zIndex: number) => void;
        };
        const icon = getFallbackIcon(isRestaurant, isSelected);

        if (typeof fallbackMarker.setIcon === 'function') {
          fallbackMarker.setIcon(icon);
        } else {
          (fallbackMarker as any).icon = icon;
        }

        const zIndexValue = isRestaurant ? 0 : (isSelected ? 2 : 1);
        if (typeof fallbackMarker.setZIndex === 'function') {
          fallbackMarker.setZIndex(zIndexValue);
        } else if ('zIndex' in fallbackMarker) {
          (fallbackMarker as any).zIndex = zIndexValue;
        }
      });
    };

    if (selectedLocationIndex === -1) {
      updateMarkerStyles(null);
      if (markerLocationsRef.current.length > 0) {
        const fallbackLocation = markerLocationsRef.current[0];
        mapInstanceRef.current.setCenter(fallbackLocation.position);
        mapInstanceRef.current.setZoom(14);
      }
      currentActivityIdRef.current = null;
      return;
    }

    updateMarkerStyles(selectedLocationIndex);

    const selectedLocation = markerLocationsRef.current[selectedLocationIndex];
    if (selectedLocation && selectedLocation.position) {
      mapInstanceRef.current.setCenter(selectedLocation.position);
      mapInstanceRef.current.setZoom(15);
      currentActivityIdRef.current = selectedActivityId;
    }
  }, [selectedActivityId, createNumberedMarkerContent]);

  // Set mounted state and cleanup
  useEffect(() => {
    isMountedRef.current = true;
    const currentUniqueId = uniqueId.current;
    return () => {
      log.debug(`[${currentUniqueId}] Component unmounting, cleaning up`);
      isMountedRef.current = false;
      setIsContainerReady(false);

      markersRef.current.forEach(marker => {
        if ('map' in marker && marker.map) {
          marker.map = null;
        } else if ('setMap' in marker) {
          (marker as any).setMap(null);
        }
      });
      markersRef.current = [];
      markerLocationsRef.current = [];
      currentActivityIdRef.current = null;
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
      supportsAdvancedMarkersRef.current = false;
      hasInitialMapRef.current = false;
      renderedActivitiesKeyRef.current = null;
      pendingActivitiesRef.current = [];
      lastUpdateTimeRef.current = 0;
      // Note: Don't clean up global loading state as other instances may be using it
    };
  }, [uniqueId]);


  // Error state - only show if container was actually visible and ready
  if (error && (isContainerReady || mapRef.current?.isConnected)) {
    return (
      <Paper
        sx={{
          height: { xs: '200px', sm: '250px', md: '300px' },
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: 2,
          flexDirection: 'column',
          gap: 2,
          p: 3
        }}
      >
        <Typography variant="h6" sx={{ color: '#856404', fontWeight: 'medium' }}>
          ⚠️ Map Error
        </Typography>
        <Typography variant="body2" sx={{ color: '#856404', textAlign: 'center' }}>
          {error}
        </Typography>
      </Paper>
    );
  }

  // If there's an error but container was never ready, it's likely a hidden instance - don't show error
  if (error && !isContainerReady && !mapRef.current?.isConnected) {
    return null;
  }

  return (
    <Box sx={{
      position: isFullscreen ? 'fixed' : 'relative',
      height: isFullscreen ? '100vh' : '100%',
      width: isFullscreen ? '100vw' : '100%',
      margin: 0,
      padding: 0,
      // Ensure map takes full container width on mobile
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      zIndex: isFullscreen ? 1300 : 'auto',
      backgroundColor: isFullscreen ? '#fff' : 'transparent',
    }}>
      <Box
        ref={mapRef}
        id={uniqueId.current}
        sx={{
          height: '100%',
          width: '100%',
          backgroundColor: '#f5f5f5',
          overflow: 'hidden',
          display: 'block',
          position: 'relative',
          margin: 0,
          padding: 0,
          // Ensure absolutely no white space on mobile
          boxSizing: 'border-box',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0
        }}
      />
      {(isLoading || !isMapApiLoaded) && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(245, 245, 245, 0.95)',
          color: '#666',
          flexDirection: 'column',
          gap: 1.5,
          borderRadius: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {!isMapApiLoaded ? 'Loading Google Maps...' : 'Creating map...'}
            </Typography>
          </Box>
          {validActivities.length > 0 ? (
            <Typography variant="caption" sx={{ color: '#888' }}>
              📍 Preparing {validActivities.length} location{validActivities.length !== 1 ? 's' : ''} for Day {dayNumber}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: '#888' }}>
              📍 Loading activity locations...
            </Typography>
          )}
        </Box>
      )}
      {isLoadingPins && isMapApiLoaded && (
        <Box sx={{ 
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#666',
          px: 2,
          py: 1,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 10
        }}>
          <CircularProgress size={16} sx={{ color: 'primary.main' }} />
          <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
            Loading pins...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DayMap; 
